import * as Sentry from '@sentry/nextjs';
import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { message } from '@vette/frontend-utils';
import { InterviewNowInput, useInterviewNowMutation } from '@vette/data-access';
import {
  date,
  env,
  jobOffer as jobOfferEntity,
  userAttribute,
} from '@vette/common-utils';
import { useApolloClient } from '@apollo/client';
import { SessionInLocalStorage } from '../types';
import { FormType } from '../form';

const APPLICANT_TIMEOUT = env.checkEnvVarValue(
  process.env.NEXT_PUBLIC_APPLICANT_TIMEOUT
);

/**
 * Handles submission of the form and stores data into local storage
 * Exposes:
 * - onSubmit: callback handling a submission of the form
 */
export const useSubmit = (
  setSessionData: (data: SessionInLocalStorage) => void
) => {
  const client = useApolloClient();
  const router = useRouter();

  const [interviewNow, { loading }] = useInterviewNowMutation();

  const storeSessionAndAttachHooks = useCallback(
    (sessionId: string) => {
      setSessionData({
        id: sessionId,
        url: router.asPath,
        expiresAt: date.utcFutureTimestamp(APPLICANT_TIMEOUT),
      });
    },
    [router.asPath, setSessionData]
  );

  const onSubmit = useCallback(
    async (
      values: FormType & Pick<InterviewNowInput, 'activity'>,
      onSubmitSuccess: () => void,
      onSubmitError: (reason: jobOfferEntity.DisableErrorReason) => void
    ) => {
      const jobOfferId = router.query?.args?.[0];
      if (!jobOfferId) return router.replace('/404');

      let remoteId;
      let source;
      if (typeof window !== 'undefined') {
        // get remoteId from url query
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('remoteId')) remoteId = urlParams.get('remoteId');
        if (urlParams.has('source')) source = urlParams.get('source');
      }

      const { interviewInSpanish, ...restOfValues } = values;
      const { data, errors } = await interviewNow({
        variables: {
          jobOfferId,
          input: {
            ...restOfValues,
            ...(interviewInSpanish && {
              attributes: [
                {
                  attributeId: userAttribute.MandatoryAttributes.language,
                  values: ['Spanish'],
                },
              ],
            }),
            remoteId,
            source,
          },
        },
      });

      if (data?.interviewNow?.__typename === 'SessionId') {
        onSubmitSuccess();
        return storeSessionAndAttachHooks(data.interviewNow.id);
      } else if (data?.interviewNow?.__typename === 'SessionInProgressError') {
        return storeSessionAndAttachHooks(data.interviewNow.sessionId);
      } else if (
        data?.interviewNow?.__typename === 'ValidationError' &&
        data.interviewNow.fields[0] === 'jobOfferId'
      ) {
        return data.interviewNow.fields[1] === 'disabledAt'
          ? onSubmitError('DISABLED')
          : onSubmitError('RAN_OUT_OF_VETTES');
      } else if (
        data?.interviewNow?.__typename === 'AttemptedToInterviewAgainError'
      ) {
        return onSubmitError('DUPLICATE_INTERVIEW');
      } else if (
        data?.interviewNow?.__typename === 'ApplicantDidNotPassKnockout'
      ) {
        return onSubmitError('APPLICANT_DID_NOT_PASS_KNOCKOUT');
      } else if (
        data?.interviewNow?.__typename === 'ApplicantIsBlackListedError'
      ) {
        return onSubmitError('APPLICANT_PHONE_IS_BLACKLISTED');
      } else if (
        data?.interviewNow?.__typename ===
        'MaximumPassedApplicantsPerJobExceededError'
      ) {
        return onSubmitError('APPLICANT_LIMIT_REACHED');
      } else if (data?.interviewNow?.__typename === 'WrongScriptVersionError') {
        client.cache.evict({ fieldName: 'jobOffer' });
        message.error(
          `There seems to be an error on our server. Please fill in the form again`
        );
        return;
      }

      if (errors) {
        for (const error of errors) {
          Sentry.captureException(error);
        }
      }
      message.error(
        'Something went wrong. Our team was notified and is intensively working on fixing the issue.'
      );
    },
    [router, storeSessionAndAttachHooks, interviewNow, client]
  );

  return {
    onSubmit,
    loading,
  };
};
